/**
 * castBehaviours — data-driven spell dispatcher.
 *
 * All numeric spell parameters live in spellBehaviourData.json.
 * This file only contains the generic behaviour handlers — one per
 * behaviourType — that read from that data.
 *
 * Adding or tweaking a spell never requires touching this file;
 * edit spellBehaviourData.json instead.
 */

import SPELL_BEHAVIOUR_DATA from './spellBehaviourData.json';
import { PLAYER_SIZE, ENEMY_SIZE, WORLD_WIDTH, WORLD_HEIGHT } from '../../constants';
import { lvl, sDmg, sRad, sCount, inCone, hitEnemy, nearestTargets } from './helpers';

// ── Behaviour handlers ────────────────────────────────────────────────────────
// Each handler receives (cfg, s, now):
//   cfg — the spell's entry from spellBehaviourData.json
//   s   — mutable game state
//   now — performance.now() timestamp

const HANDLERS = {

  /**
   * Damages all enemies within a radius around the player.
   * Supports: optional stun, stun-all-survivors (meteor), life-per-kill
   * (dark_nova), secondary visual effect (void_rift).
   */
  aoe_damage(cfg, s, now) {
    const l    = lvl(s, cfg.id);
    const dmg  = sDmg(s, cfg.dmgPerLevel[l - 1]);
    const rad  = sRad(s, cfg.radPerLevel[l - 1]);
    const stun = cfg.stunAtLevel && l >= cfg.stunAtLevel;
    const cx   = s.player.x + PLAYER_SIZE / 2;
    const cy   = s.player.y + PLAYER_SIZE / 2;

    const killsBefore = cfg.lifePerKillsAtLevel ? (s.kills ?? 0) : 0;

    s.enemies = s.enemies.map(e => {
      const sz = e.size ?? ENEMY_SIZE;
      if (Math.hypot(e.x + sz / 2 - cx, e.y + sz / 2 - cy) >= rad) return e;
      const hit = hitEnemy(s, e, dmg);
      if (hit && stun) return { ...hit, frozenUntil: Math.max(e.frozenUntil ?? 0, now + cfg.stunDuration) };
      return hit;
    }).filter(Boolean);

    if (cfg.stunAllSurvivorsAtLevel && l >= cfg.stunAllSurvivorsAtLevel) {
      s.enemies = s.enemies.map(e => ({ ...e, frozenUntil: Math.max(e.frozenUntil ?? 0, now + cfg.stunDuration) }));
    }

    if (cfg.lifePerKillsAtLevel && l >= cfg.lifePerKillsAtLevel) {
      const gained = (s.kills ?? 0) - killsBefore;
      if (gained >= cfg.lifePerKillsThreshold) {
        s.lives = Math.min(s.maxLives ?? 3, (s.lives ?? 0) + Math.floor(gained / cfg.lifePerKillsThreshold));
      }
    }

    s.spellEffects.push({ id: `se_${now}_${cfg.effectKey}`, type: cfg.effectType, x: cx, y: cy, radius: rad, startTime: now, duration: cfg.effectDuration });

    if (cfg.secondaryEffect) {
      const se = cfg.secondaryEffect;
      s.spellEffects.push({ id: `se_${now}_${se.key}`, type: se.type, x: cx, y: cy, radius: rad, startTime: now + se.offset, duration: se.duration });
    }
  },

  /**
   * Freezes all enemies for a set duration.
   * Optionally deals damage at a threshold level (ice_blast l3).
   */
  global_freeze(cfg, s, now) {
    const l   = lvl(s, cfg.id);
    const dur = cfg.durationPerLevel[l - 1];
    s.frozenUntil = Math.max(s.frozenUntil ?? 0, now + dur);
    if (cfg.dmgAtLevel && l >= cfg.dmgAtLevel) {
      s.enemies = s.enemies.map(e => hitEnemy(s, e, sDmg(s, cfg.dmgBase))).filter(Boolean);
    }
    s.spellEffects.push({ id: `se_${now}_${cfg.effectKey}`, type: cfg.effectType, x: WORLD_WIDTH / 2, y: WORLD_HEIGHT / 2, startTime: now, duration: dur });
  },

  /**
   * Teleports the player to the centre of the map.
   * Optionally deals AoE damage at the origin from a threshold level,
   * and grants a shield at a higher threshold level.
   */
  teleport(cfg, s, now) {
    const l  = lvl(s, cfg.id);
    const ox = s.player.x;
    const oy = s.player.y;
    s.player.x = WORLD_WIDTH  / 2 - PLAYER_SIZE / 2;
    s.player.y = WORLD_HEIGHT / 2 - PLAYER_SIZE / 2;

    if (cfg.aoeAtLevel && l >= cfg.aoeAtLevel) {
      const cx       = ox + PLAYER_SIZE / 2;
      const cy       = oy + PLAYER_SIZE / 2;
      const dmg      = sDmg(s, cfg.dmgPerLevel[l - 1]);
      const blinkRad = sRad(s, cfg.blinkRadBase);
      s.enemies = s.enemies.map(e => {
        const sz = e.size ?? ENEMY_SIZE;
        return Math.hypot(e.x + sz / 2 - cx, e.y + sz / 2 - cy) < blinkRad ? hitEnemy(s, e, dmg) : e;
      }).filter(Boolean);
    }

    if (cfg.shieldAtLevel && l >= cfg.shieldAtLevel) {
      s.shieldUntil = Math.max(s.shieldUntil ?? 0, now + cfg.shieldDuration);
    }

    s.spellEffects.push({ id: `se_${now}_${cfg.effectKey}1`, type: cfg.effectType, x: ox + PLAYER_SIZE / 2, y: oy + PLAYER_SIZE / 2, startTime: now, duration: cfg.effectDuration });
    s.spellEffects.push({ id: `se_${now}_${cfg.effectKey}2`, type: cfg.effectType, x: WORLD_WIDTH / 2, y: WORLD_HEIGHT / 2, startTime: now + cfg.effectOffset, duration: cfg.effectDuration });
  },

  /**
   * Pulls all enemies toward the player.
   * Optionally deals damage and stuns at threshold levels.
   */
  pull_enemies(cfg, s, now) {
    const l      = lvl(s, cfg.id);
    const pull   = sRad(s, cfg.pullPerLevel[l - 1]);
    const rawDmg = cfg.dmgPerLevel[l - 1];
    const stun   = cfg.stunAtLevel && l >= cfg.stunAtLevel;
    const cx     = s.player.x + PLAYER_SIZE / 2;
    const cy     = s.player.y + PLAYER_SIZE / 2;

    s.enemies = s.enemies.map(e => {
      const sz   = e.size ?? ENEMY_SIZE;
      const ex   = e.x + sz / 2;
      const ey   = e.y + sz / 2;
      const dist = Math.hypot(ex - cx, ey - cy);
      if (dist < 5) return e;
      const moved = { ...e,
        x: e.x + (cx - ex) / dist * Math.min(pull, dist * 0.75),
        y: e.y + (cy - ey) / dist * Math.min(pull, dist * 0.75),
      };
      if (rawDmg > 0) {
        const hit = hitEnemy(s, moved, sDmg(s, rawDmg));
        if (!hit) return null;
        return stun ? { ...hit, frozenUntil: Math.max(hit.frozenUntil ?? 0, now + cfg.stunDuration) } : hit;
      }
      return stun ? { ...moved, frozenUntil: Math.max(moved.frozenUntil ?? 0, now + cfg.stunDuration) } : moved;
    }).filter(Boolean);

    s.spellEffects.push({ id: `se_${now}_${cfg.effectKey}`, type: cfg.effectType, x: cx, y: cy, startTime: now, duration: cfg.effectDuration });
  },

  /**
   * Slows all enemies to a fraction of their speed for a duration.
   * Optionally deals damage at a threshold level.
   */
  global_slow(cfg, s, now) {
    const l = lvl(s, cfg.id);
    const dur = cfg.durationPerLevel[l - 1];
    s.slowTimeUntil  = Math.max(s.slowTimeUntil ?? 0, now + dur);
    s.slowTimeFactor = cfg.factorPerLevel[l - 1];
    if (cfg.dmgAtLevel && l >= cfg.dmgAtLevel) {
      s.enemies = s.enemies.map(e => hitEnemy(s, e, sDmg(s, cfg.dmgBase))).filter(Boolean);
    }
    s.spellEffects.push({ id: `se_${now}_${cfg.effectKey}`, type: cfg.effectType, x: WORLD_WIDTH / 2, y: WORLD_HEIGHT / 2, startTime: now, duration: dur });
  },

  /**
   * Deals damage to the N nearest enemies.
   * Used by thunder, chain_lightning, and arcane_barrage.
   */
  nearest_damage(cfg, s, now) {
    if (!s.enemies.length) return;
    const l       = lvl(s, cfg.id);
    const dmg     = sDmg(s, cfg.dmgPerLevel[l - 1]);
    const count   = sCount(s, cfg.countPerLevel[l - 1]);
    const cx      = s.player.x + PLAYER_SIZE / 2;
    const cy      = s.player.y + PLAYER_SIZE / 2;
    const targets = nearestTargets(s, cx, cy, count);
    const hitIds  = new Set(targets.map(e => e.id));
    s.enemies = s.enemies.map(e => hitIds.has(e.id) ? hitEnemy(s, e, dmg) : e).filter(Boolean);
    targets.forEach((e, i) => s.spellEffects.push({
      id:        `se_${now}_${cfg.effectKey}${i}`,
      type:      cfg.effectType,
      x:         e.x + (e.size ?? ENEMY_SIZE) / 2,
      y:         e.y + (e.size ?? ENEMY_SIZE) / 2,
      startTime: now + i * cfg.effectStagger,
      duration:  cfg.effectDuration,
    }));
  },

  /**
   * Deals damage and freezes the N nearest enemies.
   * Freeze duration and count both scale per level (glacial_storm).
   * countPerLevel entry 9999 means "all enemies".
   */
  nearest_damage_freeze(cfg, s, now) {
    const l      = lvl(s, cfg.id);
    const count  = sCount(s, cfg.countPerLevel[l - 1]);
    const dmg    = sDmg(s, cfg.dmgPerLevel[l - 1]);
    const freeze = cfg.freezePerLevel[l - 1];
    const cx     = s.player.x + PLAYER_SIZE / 2;
    const cy     = s.player.y + PLAYER_SIZE / 2;
    const targets = nearestTargets(s, cx, cy, count);
    const hitIds  = new Set(targets.map(e => e.id));
    s.enemies = s.enemies.map(e => {
      if (!hitIds.has(e.id)) return e;
      const hit = hitEnemy(s, e, dmg);
      return hit ? { ...hit, frozenUntil: Math.max(hit.frozenUntil ?? 0, now + freeze) } : null;
    }).filter(Boolean);
    s.spellEffects.push({ id: `se_${now}_${cfg.effectKey}`, type: cfg.effectType, x: cx, y: cy, startTime: now, duration: freeze });
  },

  /**
   * Deals damage to the N nearest enemies and restores lives to the player.
   * Shows a player-centre heal effect plus per-target secondary effects (soul_drain).
   */
  nearest_damage_drain(cfg, s, now) {
    if (!s.enemies.length) return;
    const l       = lvl(s, cfg.id);
    const dmg     = sDmg(s, cfg.dmgPerLevel[l - 1]);
    const count   = sCount(s, cfg.countPerLevel[l - 1]);
    const lives   = cfg.livesPerLevel[l - 1];
    const cx      = s.player.x + PLAYER_SIZE / 2;
    const cy      = s.player.y + PLAYER_SIZE / 2;
    const targets = nearestTargets(s, cx, cy, count);
    const hitIds  = new Set(targets.map(e => e.id));
    s.enemies = s.enemies.map(e => hitIds.has(e.id) ? hitEnemy(s, e, dmg) : e).filter(Boolean);
    s.lives = Math.min(s.maxLives ?? 3, (s.lives ?? 0) + lives);
    s.spellEffects.push({ id: `se_${now}_${cfg.effectKey}`,  type: cfg.effectType, x: cx, y: cy, startTime: now, duration: cfg.effectDuration });
    targets.forEach((e, i) => s.spellEffects.push({
      id:        `se_${now}_${cfg.effectKey}${i}`,
      type:      cfg.secondaryEffectType,
      x:         e.x + (e.size ?? ENEMY_SIZE) / 2,
      y:         e.y + (e.size ?? ENEMY_SIZE) / 2,
      startTime: now + i * cfg.secondaryStagger,
      duration:  cfg.secondaryEffectDuration,
    }));
  },

  /**
   * Deals damage to the N nearest enemies, restores lives, and applies
   * optional AoE splash damage to surrounding enemies (blood_frenzy).
   */
  nearest_frenzy(cfg, s, now) {
    const l         = lvl(s, cfg.id);
    const dmg       = sDmg(s, cfg.dmgPerLevel[l - 1]);
    const count     = sCount(s, cfg.countPerLevel[l - 1]);
    const lives     = cfg.livesPerLevel[l - 1];
    const rawSplash = cfg.splashAtLevel && l >= cfg.splashAtLevel ? cfg.splashPerLevel[l - 1] : 0;
    const splash    = rawSplash > 0 ? sDmg(s, rawSplash) : 0;
    const splashRad = sRad(s, cfg.splashRadBase ?? 0);
    const cx        = s.player.x + PLAYER_SIZE / 2;
    const cy        = s.player.y + PLAYER_SIZE / 2;
    const targets   = nearestTargets(s, cx, cy, count);
    targets.forEach((e, i) => s.spellEffects.push({
      id:        `se_${now}_${cfg.effectKey}${i}`,
      type:      cfg.effectType,
      x:         e.x + (e.size ?? ENEMY_SIZE) / 2,
      y:         e.y + (e.size ?? ENEMY_SIZE) / 2,
      startTime: now + i * cfg.effectStagger,
      duration:  cfg.effectDuration,
    }));
    const hitIds = new Set(targets.map(e => e.id));
    s.enemies = s.enemies.map(e => {
      const sz = e.size ?? ENEMY_SIZE;
      if (hitIds.has(e.id)) return hitEnemy(s, e, dmg);
      if (splash > 0 && Math.hypot(e.x + sz / 2 - cx, e.y + sz / 2 - cy) < splashRad) return hitEnemy(s, e, splash);
      return e;
    }).filter(Boolean);
    s.lives = Math.min(s.maxLives ?? 3, (s.lives ?? 0) + lives);
  },

  /**
   * Grants the player a timed invincibility shield.
   * Optionally repulses nearby enemies at a threshold level (barrier l3).
   */
  shield_self(cfg, s, now) {
    const l   = lvl(s, cfg.id);
    const dur = cfg.durationPerLevel[l - 1];
    s.shieldUntil = Math.max(s.shieldUntil ?? 0, now + dur);
    if (cfg.repulseAtLevel && l >= cfg.repulseAtLevel) {
      const repulse = sRad(s, cfg.repulseRadBase);
      const cx      = s.player.x + PLAYER_SIZE / 2;
      const cy      = s.player.y + PLAYER_SIZE / 2;
      s.enemies = s.enemies.map(e => {
        const sz   = e.size ?? ENEMY_SIZE;
        const ex   = e.x + sz / 2;
        const ey   = e.y + sz / 2;
        const dist = Math.hypot(ex - cx, ey - cy);
        if (dist < 1) return e;
        return { ...e,
          x: Math.max(0, Math.min(WORLD_WIDTH  - sz, e.x + (ex - cx) / dist * repulse)),
          y: Math.max(0, Math.min(WORLD_HEIGHT - sz, e.y + (ey - cy) / dist * repulse)),
        };
      });
    }
    s.spellEffects.push({ id: `se_${now}_${cfg.effectKey}`, type: cfg.effectType, x: s.player.x + PLAYER_SIZE / 2, y: s.player.y + PLAYER_SIZE / 2, startTime: now, duration: dur });
  },

  /**
   * Blasts all enemies away from the player.
   * Optionally deals damage on impact and stuns at threshold levels (repulse).
   */
  push_enemies(cfg, s, now) {
    const l      = lvl(s, cfg.id);
    const push   = sRad(s, cfg.pushPerLevel[l - 1]);
    const rawDmg = cfg.dmgPerLevel[l - 1];
    const stun   = cfg.stunAtLevel && l >= cfg.stunAtLevel;
    const cx     = s.player.x + PLAYER_SIZE / 2;
    const cy     = s.player.y + PLAYER_SIZE / 2;

    s.enemies = s.enemies.map(e => {
      const sz   = e.size ?? ENEMY_SIZE;
      const ex   = e.x + sz / 2;
      const ey   = e.y + sz / 2;
      const dist = Math.hypot(ex - cx, ey - cy);
      if (dist < 1) return e;
      const pushed = { ...e,
        x: Math.max(0, Math.min(WORLD_WIDTH  - sz, e.x + (ex - cx) / dist * push)),
        y: Math.max(0, Math.min(WORLD_HEIGHT - sz, e.y + (ey - cy) / dist * push)),
      };
      if (rawDmg > 0) {
        const hit = hitEnemy(s, pushed, sDmg(s, rawDmg));
        if (!hit) return null;
        return stun ? { ...hit, frozenUntil: Math.max(hit.frozenUntil ?? 0, now + cfg.stunDuration) } : hit;
      }
      return stun ? { ...pushed, frozenUntil: Math.max(pushed.frozenUntil ?? 0, now + cfg.stunDuration) } : pushed;
    }).filter(Boolean);

    s.spellEffects.push({ id: `se_${now}_${cfg.effectKey}`, type: cfg.effectType, x: cx, y: cy, radius: sRad(s, cfg.effectRadBase), startTime: now, duration: cfg.effectDuration });
  },

  /**
   * Deals damage to all enemies inside a directional cone.
   * Optionally stuns / burns hit enemies at a threshold level.
   * Used by flame_breath and death_ray.
   */
  cone_damage(cfg, s, now) {
    const l      = lvl(s, cfg.id);
    const dmg    = sDmg(s, cfg.dmgPerLevel[l - 1]);
    const range  = sRad(s, cfg.rangePerLevel[l - 1]);
    const stun   = cfg.stunAtLevel && l >= cfg.stunAtLevel;
    const cx     = s.player.x + PLAYER_SIZE / 2;
    const cy     = s.player.y + PLAYER_SIZE / 2;
    const facing = s.playerFacingAngle ?? -Math.PI / 2;

    s.enemies = s.enemies.map(e => {
      const sz = e.size ?? ENEMY_SIZE;
      if (!inCone(e.x + sz / 2, e.y + sz / 2, cx, cy, facing, cfg.halfAngleRad, range)) return e;
      const hit = hitEnemy(s, e, dmg);
      if (hit && stun) return { ...hit, frozenUntil: Math.max(hit.frozenUntil ?? 0, now + cfg.stunDuration) };
      return hit;
    }).filter(Boolean);

    s.spellEffects.push({ id: `se_${now}_${cfg.effectKey}`, type: 'cone', x: cx, y: cy,
      angle: facing, halfAngle: cfg.halfAngleRad, range,
      color: cfg.effectColor, strokeColor: cfg.effectStrokeColor,
      startTime: now, duration: cfg.effectDuration });
  },

  /**
   * Deals damage and knocks enemies back inside a directional cone.
   * Optionally stuns survivors at a threshold level (shockwave).
   */
  cone_knockback(cfg, s, now) {
    const l      = lvl(s, cfg.id);
    const dmg    = sDmg(s, cfg.dmgPerLevel[l - 1]);
    const range  = sRad(s, cfg.rangePerLevel[l - 1]);
    const push   = cfg.pushPerLevel[l - 1];
    const stun   = cfg.stunAtLevel && l >= cfg.stunAtLevel;
    const cx     = s.player.x + PLAYER_SIZE / 2;
    const cy     = s.player.y + PLAYER_SIZE / 2;
    const facing = s.playerFacingAngle ?? -Math.PI / 2;

    s.enemies = s.enemies.map(e => {
      const sz   = e.size ?? ENEMY_SIZE;
      const ex   = e.x + sz / 2;
      const ey   = e.y + sz / 2;
      if (!inCone(ex, ey, cx, cy, facing, cfg.halfAngleRad, range)) return e;
      const dx   = ex - cx;
      const dy   = ey - cy;
      const dist = Math.hypot(dx, dy) || 1;
      const knocked = { ...e,
        x: Math.max(0, Math.min(WORLD_WIDTH  - sz, e.x + (dx / dist) * push)),
        y: Math.max(0, Math.min(WORLD_HEIGHT - sz, e.y + (dy / dist) * push)),
      };
      const hit = hitEnemy(s, knocked, dmg);
      if (!hit) return null;
      return stun ? { ...hit, frozenUntil: Math.max(hit.frozenUntil ?? 0, now + cfg.stunDuration) } : hit;
    }).filter(Boolean);

    s.spellEffects.push({ id: `se_${now}_${cfg.effectKey}`, type: 'cone', x: cx, y: cy,
      angle: facing, halfAngle: cfg.halfAngleRad, range,
      color: cfg.effectColor, strokeColor: cfg.effectStrokeColor,
      startTime: now, duration: cfg.effectDuration });
  },

  /**
   * Restores lives and heals a percentage of max HP.
   * healPctPerLevel 1.00 → full HP restore.
   * Optionally grants a shield at a threshold level (mend l3).
   */
  heal_self(cfg, s, now) {
    const l       = lvl(s, cfg.id);
    const lives   = cfg.livesPerLevel[l - 1];
    const healPct = cfg.healPctPerLevel[l - 1];
    s.lives = Math.min(s.maxLives ?? 3, (s.lives ?? 0) + lives);
    s.hp    = healPct >= 1.0
      ? (s.maxHp ?? 100)
      : Math.min(s.maxHp ?? 100, (s.hp ?? 0) + Math.round((s.maxHp ?? 100) * healPct));
    if (cfg.shieldAtLevel && l >= cfg.shieldAtLevel) {
      s.shieldUntil = Math.max(s.shieldUntil ?? 0, now + cfg.shieldDuration);
    }
    s.spellEffects.push({ id: `se_${now}_${cfg.effectKey}`, type: cfg.effectType, x: s.player.x + PLAYER_SIZE / 2, y: s.player.y + PLAYER_SIZE / 2, startTime: now, duration: cfg.effectDuration });
  },

  /**
   * Deals damage to every enemy on screen, grants a shield, and optionally
   * restores lives at higher levels (apocalypse).
   */
  screen_wipe(cfg, s, now) {
    const l      = lvl(s, cfg.id);
    const dmg    = sDmg(s, cfg.dmgPerLevel[l - 1]);
    const shield = cfg.shieldPerLevel[l - 1];
    const lives  = cfg.livesPerLevel[l - 1];
    s.enemies = s.enemies.map(e => hitEnemy(s, e, dmg)).filter(Boolean);
    s.shieldUntil = Math.max(s.shieldUntil ?? 0, now + shield);
    if (lives > 0) s.lives = Math.min(s.maxLives ?? 3, (s.lives ?? 0) + lives);
    const cx = s.player.x + PLAYER_SIZE / 2;
    const cy = s.player.y + PLAYER_SIZE / 2;
    s.spellEffects.push({ id: `se_${now}_${cfg.effectKey}1`, type: cfg.effectType,  x: cx, y: cy, radius: cfg.effectRadius, startTime: now,       duration: cfg.effectDuration });
    s.spellEffects.push({ id: `se_${now}_${cfg.effectKey}2`, type: 'shield',        x: cx, y: cy,                           startTime: now + 200, duration: shield });
  },

  /**
   * Freezes and slows all enemies, grants the player a shield, and optionally
   * deals damage and teleports to centre at higher levels (time_stop).
   */
  freeze_slow_shield(cfg, s, now) {
    const l      = lvl(s, cfg.id);
    const dur    = cfg.durationPerLevel[l - 1];
    const shield = cfg.shieldPerLevel[l - 1];
    const dmg    = cfg.dmgAtLevel && l >= cfg.dmgAtLevel ? sDmg(s, cfg.dmgPerLevel[l - 1]) : 0;
    s.frozenUntil    = Math.max(s.frozenUntil    ?? 0, now + dur);
    s.slowTimeUntil  = Math.max(s.slowTimeUntil  ?? 0, now + dur);
    s.slowTimeFactor = cfg.slowFactor;
    s.shieldUntil    = Math.max(s.shieldUntil    ?? 0, now + shield);
    if (dmg > 0) s.enemies = s.enemies.map(e => hitEnemy(s, e, dmg)).filter(Boolean);
    if (cfg.teleportAtLevel && l >= cfg.teleportAtLevel) {
      s.player.x = WORLD_WIDTH  / 2 - PLAYER_SIZE / 2;
      s.player.y = WORLD_HEIGHT / 2 - PLAYER_SIZE / 2;
    }
    s.spellEffects.push({ id: `se_${now}_${cfg.effectKey}`,  type: 'freeze', x: WORLD_WIDTH / 2, y: WORLD_HEIGHT / 2, startTime: now, duration: dur });
    s.spellEffects.push({ id: `se_${now}_${cfg.effectKey}2`, type: 'slow',   x: WORLD_WIDTH / 2, y: WORLD_HEIGHT / 2, startTime: now, duration: dur });
  },

};

// ── Assemble cast functions ───────────────────────────────────────────────────

/**
 * One cast function per spell id, built by binding each spell's
 * data record from spellBehaviourData.json to its behaviour handler.
 *
 * Shape: { [spellId]: (s, now) => void }
 */
const CAST_BEHAVIOURS = Object.fromEntries(
  SPELL_BEHAVIOUR_DATA.map(cfg => [
    cfg.id,
    (s, now) => HANDLERS[cfg.behaviourType](cfg, s, now),
  ])
);

export default CAST_BEHAVIOURS;
